import CallIcon from "@mui/icons-material/Call";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PoolIcon from "@mui/icons-material/Pool";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import { areasDataDetailed } from "../../data/areasData";

import ArchitectureIcon from "@mui/icons-material/Architecture";
import BalconyIcon from "@mui/icons-material/Balcony";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import MovieIcon from "@mui/icons-material/Movie";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";

const iconMap = [
  { label: "Swimming pools & fitness centers", icon: <PoolIcon /> },
  { label: "Children’s play areas", icon: <ChildCareIcon /> },
  { label: "Central Hub by Zaha Hadid Architects", icon: <ArchitectureIcon /> },
  { label: "Retail & dining outlets", icon: <StoreMallDirectoryIcon /> },
  { label: "Drive-in cinema & cultural attractions", icon: <MovieIcon /> },
];

const CONTACT = {
  phone: "+971547914848",
  whatsapp: "+971547914848",
};

export default function AreaDetailsModal({ open, onClose, area }) {
  if (!area) return null;
  const details = areasDataDetailed?.find((d) => d?.id === area?.id);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(10px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        {/* STOP CLICK PROPAGATION */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: "100%",
            maxWidth: 860,
            height: { xs: "90vh", md: "82vh" },
            backgroundColor: "#fff",
            borderRadius: "26px",
            overflow: "hidden",
            boxShadow: "0 40px 120px rgba(0,0,0,0.35)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER IMAGE */}
          <Box
            sx={{
              position: "relative",
              height: { xs: 220, md: 280 },
              overflow: "hidden",
              backgroundColor: "#000",
            }}
          >
            {/* 🔹 BLURRED BACKGROUND IMAGE */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${area.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(3px)",
                transform: "scale(1.2)", // avoids edge blur cutoff
                // opacity: 0.6,
              }}
            />

            {/* 🔹 DARK OVERLAY (FOR READABILITY) */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.65))",
                zIndex: 1,
              }}
            />

            {/* 🔹 MAIN IMAGE (FULLY VISIBLE) */}
            <Box
              component="img"
              src={area.image}
              alt={area.title}
              sx={{
                position: "relative",
                zIndex: 2,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />

            {/* 🔹 CLOSE BUTTON */}
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 14,
                right: 14,
                zIndex: 3,
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                backdropFilter: "blur(6px)",
                "&:hover": { background: "rgba(0,0,0,0.75)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* 🔹 TITLE */}
            <Box sx={{ position: "absolute", bottom: 20, left: 20, zIndex: 3 }}>
              <Typography sx={{ color: "#fff", fontSize: 22, fontWeight: 600 }}>
                {area.title}
              </Typography>

              {details?.tagline && (
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: 13,
                    mt: 0.5,
                    letterSpacing: "0.06em",
                  }}
                >
                  {details.tagline}
                </Typography>
              )}
            </Box>
          </Box>

          {/* SCROLLABLE CONTENT */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 3, md: 4 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Typography
              sx={{
                fontSize: 15,
                lineHeight: 1.9,
                color: "var(--color-text-muted)",
                mb: 3,
              }}
            >
              {details?.overview}
            </Typography>
            {details?.propertyInfo && (
              <>
                <Typography sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>
                  Property Information
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 1.5,
                    mb: 3,
                  }}
                >
                  {Object.entries(details.propertyInfo).map(([key, value]) => (
                    <Box key={key}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "var(--color-text-muted)",
                          textTransform: "capitalize",
                        }}
                      >
                        {key.replace(/([A-Z])/g, " $1")}
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
            {details?.amenities && (
              <AmenitiesSection amenities={details?.amenities} />
            )}
            {details?.highlights && (
              <>
                <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                  Key Highlights
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  {details?.highlights?.map((item, i) => (
                    <li key={i}>
                      <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>
                        {item}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </>
            )}
            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>About us</Typography>
            <Typography
              sx={{
                fontSize: 15,
                lineHeight: 1.9,
                color: "var(--color-text-muted)",
                mb: 3,
              }}
            >
              Vision X Nexus Real Estate LLC is a dynamic and innovative real
              estate company dedicated to providing exceptional services and
              tailored solutions to our clients. We specialize in residential
              and commercial real estate, offering services such as property
              management, sales, leasing, and investment opportunities. With our
              industry expertise, customized approach, technology integration,
              and market intelligence, we strive to exceed expectations and
              shape the future of real estate. Trust us to be your reliable
              partner in turning your vision into reality.
            </Typography>
          </Box>

          {/* FIXED ACTION BAR */}
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              borderTop: "1px solid rgba(0,0,0,0.08)",
              background: "#fff",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  md: "repeat(4, 1fr)",
                },
                gap: 1.2,
              }}
            >
              <Button
                href={area.viewLink}
                target="_blank"
                startIcon={<VisibilityOutlinedIcon />}
                sx={actionBtn("gold")}
              >
                View
              </Button>

              <Button
                href={area.mapLink}
                target="_blank"
                startIcon={<LocationOnOutlinedIcon />}
                sx={actionBtn("outline")}
              >
                Direction
              </Button>

              <Button
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                startIcon={<WhatsAppIcon />}
                sx={actionBtn("whatsapp")}
              >
                WhatsApp
              </Button>

              <Button
                href={`tel:${CONTACT.phone}`}
                startIcon={<CallIcon />}
                sx={actionBtn("dark")}
              >
                Call
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

/* BUTTON STYLES */
const actionBtn = (type) => ({
  height: 44,
  borderRadius: "14px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  gap: 0.8,
  boxShadow: "0 6px 18px rgba(0,0,0,0.12)",

  "& .MuiButton-startIcon": {
    marginRight: "6px",
  },

  ...(type === "gold" && {
    background: "var(--color-gold)",
    color: "#111",
    "&:hover": { background: "var(--color-gold)" },
  }),

  ...(type === "outline" && {
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.18)",
    color: "#111",
  }),

  ...(type === "whatsapp" && {
    background: "#25D366",
    color: "#fff",
    "&:hover": { background: "#1ebe5b" },
  }),

  ...(type === "dark" && {
    background: "#111",
    color: "#fff",
  }),
});

function AmenitiesSection({ amenities }) {
  const visible = amenities?.slice(0, 5);
  const remaining = amenities?.length - visible?.length;

  return (
    <Box mt={4} mb={4}>
      {/* TITLE */}
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 600,
          mb: 2,
        }}
      >
        Features & Amenities
      </Typography>

      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(5, 1fr)",
          },
          gap: 2,
        }}
      >
        {visible.map((item, i) => (
          <Box
            key={i}
            sx={{
              borderRadius: "18px",
              padding: "18px 14px",
              background: "linear-gradient(180deg, #ffffff, #f7f7f7)",
              textAlign: "center",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              transition: "all 0.35s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 20px 45px rgba(0,0,0,0.12)",
              },
            }}
          >
            {/* ICON */}
            <Box
              sx={{
                width: 32,
                height: 32,
                mx: "auto",
                mb: 1.2,
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, var(--color-gold), #e7c16a)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#111",
              }}
            >
              {iconMap[item] || <BalconyIcon />}
            </Box>

            {/* TEXT */}
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: 1.5,
                color: "#222",
                fontWeight: 500,
              }}
            >
              {item}
            </Typography>
          </Box>
        ))}

        {/* MORE */}
        {remaining > 0 && (
          <Box
            sx={{
              borderRadius: "18px",
              padding: "18px 14px",
              background: "#fff",
              textAlign: "center",
              border: "1px dashed rgba(0,0,0,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--color-gold)",
              }}
            >
              + {remaining} more amenities
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
